// Manifest for the design editor. Mirrors the template's token defaults
// (apps/template/src/styles/tokens.css) so the form can show each token's real
// value as a placeholder and render an accurate preview. Overrides are stored
// keyed by token name WITHOUT the leading "--".

const FONT_BODY =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export interface TokenSelectOption {
  label: string;
  value: string;
}

export interface TokenField {
  key: string;
  label: string;
  type: "color" | "font" | "text" | "scale" | "select";
  /** Choices for type "select"; the field also always offers "Default". */
  options?: TokenSelectOption[];
}

export interface TokenGroup {
  id: string;
  label: string;
  /** One-line explanation shown under the group heading. */
  description?: string;
  fields: TokenField[];
}

// Theme-agnostic system scale (tokens.css :root, minus colours/radius).
const SYSTEM_DEFAULTS: Record<string, string> = {
  "font-body": FONT_BODY,
  "font-weight-normal": "400",
  "font-weight-medium": "500",
  "font-weight-semibold": "600",
  "font-weight-bold": "700",
  "heading-weight": "700",
  "heading-style": "normal",
  "heading-case": "none",
  "heading-tracking": "normal",
  "leading-tight": "1.2",
  "leading-normal": "1.6",
  "font-size-xs": "0.875rem",
  "font-size-base": "1rem",
  "typography-scale": "1.200",
  // Heading and display sizes at the default Minor Third (1.200) scale, h6 one
  // step above the 1rem base and the display sizes one…four steps above h1;
  // recomputed from the min/max typography scales on save (see
  // lib/derived-tokens.ts).
  "display-1": "6.192rem",
  "display-2": "5.16rem",
  "display-3": "4.3rem",
  "display-4": "3.583rem",
  h1: "2.986rem",
  h2: "2.488rem",
  h3: "2.074rem",
  h4: "1.728rem",
  h5: "1.44rem",
  h6: "1.2rem",
  "space-1": "0.25rem",
  "space-2": "0.5rem",
  "space-3": "0.75rem",
  "space-4": "1rem",
  "space-5": "1.25rem",
  "space-6": "1.5rem",
  "space-7": "1.75rem",
  "space-8": "2rem",
  "space-10": "2.5rem",
  "space-12": "3rem",
  "space-16": "4rem",
  "shadow-sm": "0 1px 2px rgba(0, 0, 0, 0.06)",
  "shadow-md": "0 4px 12px rgba(0, 0, 0, 0.1)",
  "transition-fast": "0.1s",
  "transition-base": "0.15s",
  "container-max": "80rem",
  "container-pad": "1.5rem",
};

// Base brand tokens (colours + radius + heading font) — the template's :root
// palette in styles/tokens.css. Per-tenant overrides are layered on top.
const BRAND_DEFAULTS: Record<string, string> = {
  bg: "#ffffff", "bg-alt": "#f7f7f7", text: "#1a1a1a", "text-muted": "#666666",
  accent: "#2563eb", "accent-hover": "#1d4ed8", "hero-bg": "#1a1a1a", "hero-text": "#ffffff",
  "header-bg": "#ffffff", "header-text": "#1a1a1a", "footer-bg": "#1a1a1a", "footer-text": "#e5e5e5",
  border: "#e5e5e5", radius: "6px", "font-heading": FONT_BODY,
};

/** Default value for every editable token (system scale + base brand palette). */
export const TOKEN_DEFAULTS: Record<string, string> = { ...SYSTEM_DEFAULTS, ...BRAND_DEFAULTS };

// The tokens that are no longer edited directly: they are calculated from the
// brand palette and the accessibility target (see lib/derived-tokens.ts) and
// written into design_tokens on save so the published site keeps working
// without template changes.
export const DERIVED_COLOR_KEYS = [
  "accent-hover",
  "bg-alt",
  "text",
  "text-muted",
  "border",
  "header-text",
  "footer-text",
  "hero-bg",
  "hero-text",
] as const;

// Typography tokens computed from the typography-scale(-max) tokens on save:
// the heading and display sizes plus the pinned base/XS body sizes.
export const DERIVED_TYPOGRAPHY_KEYS = [
  "display-1",
  "display-2",
  "display-3",
  "display-4",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "font-size-base",
  "font-size-xs",
] as const;

export const DERIVED_TOKEN_KEYS: readonly string[] = [
  ...DERIVED_COLOR_KEYS,
  ...DERIVED_TYPOGRAPHY_KEYS,
];

// Retired font-size tokens the template no longer reads; stripped from stored
// overrides on load so old tenants don't carry them forever.
export const LEGACY_TOKEN_KEYS = [
  "font-size-md",
  "font-size-lg",
  "font-size-xl",
  "font-size-2xl",
  "font-size-3xl",
  "font-size-4xl",
] as const;

// The predefined typographic scales the heading sizes multiply by, in
// ascending ratio order. Any other value in [1, 2] is a "Custom" scale.
export const TYPOGRAPHY_SCALES: readonly { label: string; value: number }[] = [
  { label: "Minor Second", value: 1.067 },
  { label: "Major Second", value: 1.125 },
  { label: "Minor Third", value: 1.2 },
  { label: "Major Third", value: 1.25 },
  { label: "Perfect Fourth", value: 1.333 },
  { label: "Augmented Fourth", value: 1.414 },
  { label: "Perfect Fifth", value: 1.5 },
  { label: "Golden Ratio", value: 1.618 },
];

/** Typography scale bounds enforced by the editor and the derivation. */
export const TYPOGRAPHY_SCALE_MIN = 1;
export const TYPOGRAPHY_SCALE_MAX = 2;

// Optional per-tenant heading style overrides (font-heading already carries
// the family). Every h1-h6 on the published site reads these; unset falls
// back to the defaults above (bold, upright, no casing change, no tracking).
export const HEADING_WEIGHT_OPTIONS: readonly TokenSelectOption[] = [
  { label: "Regular", value: "400" },
  { label: "Medium", value: "500" },
  { label: "Semibold", value: "600" },
  { label: "Bold", value: "700" },
  { label: "Extrabold", value: "800" },
  { label: "Black", value: "900" },
];

export const HEADING_STYLE_OPTIONS: readonly TokenSelectOption[] = [
  { label: "Normal", value: "normal" },
  { label: "Italic", value: "italic" },
];

export const HEADING_CASE_OPTIONS: readonly TokenSelectOption[] = [
  { label: "None", value: "none" },
  { label: "UPPERCASE", value: "uppercase" },
  { label: "lowercase", value: "lowercase" },
  { label: "Capitalize", value: "capitalize" },
];

// The viewport range the fluid type sizes interpolate across: the minimum
// scale applies at 360px (22.5rem) and below, the maximum at 1280px (80rem,
// the default --container-max) and above, tablet landing in between.
export const FLUID_VIEWPORT_MIN_REM = 22.5;
export const FLUID_VIEWPORT_MAX_REM = 80;

// The token recording the tenant's WCAG target. Stored inside design_tokens so
// it needs no schema change; harmless when rendered as a custom property.
export const A11Y_TARGET_TOKEN = "a11y-target";

export type A11yTarget = "aa" | "aaa";

/** The minimum contrast ratio (normal text) each target requires. */
export const A11Y_MIN_RATIO: Record<A11yTarget, number> = { aa: 4.5, aaa: 7 };

/** Normalise a stored a11y-target token value ("aa" unless explicitly "aaa"). */
export function parseA11yTarget(raw: string | undefined): A11yTarget {
  return raw?.trim().toLowerCase() === "aaa" ? "aaa" : "aa";
}

// Editable tokens grouped by area of the site (global brand, then per-region,
// then cross-cutting scales). Rendered as one editor with a section per area.
// Colours removed from these groups are in DERIVED_TOKEN_KEYS instead.
export const TOKEN_GROUPS: TokenGroup[] = [
  {
    id: "global",
    label: "Global",
    description:
      "Accent inherits the Primary Brand Colour unless set here; the page background follows the Base Background Colour.",
    fields: [
      { key: "accent", label: "Accent", type: "color" },
      { key: "radius", label: "Corner radius", type: "text" },
    ],
  },
  {
    id: "header",
    label: "Header",
    description: "Header text colour is calculated from the background to meet the accessibility target.",
    fields: [{ key: "header-bg", label: "Background", type: "color" }],
  },
  {
    id: "footer",
    label: "Footer",
    description: "Footer text colour is calculated from the background to meet the accessibility target.",
    fields: [{ key: "footer-bg", label: "Background", type: "color" }],
  },
  {
    id: "typography",
    label: "Typography",
    description:
      "Fonts and the type scale. Heading and display sizes are calculated from the scale ratio " +
      "off a 1rem base, growing from the minimum scale at 360px wide to the maximum at 1280px. " +
      "The heading style fields are optional — leave them on Default to inherit the theme.",
    fields: [
      { key: "font-heading", label: "Heading font", type: "font" },
      { key: "heading-weight", label: "Heading weight", type: "select", options: [...HEADING_WEIGHT_OPTIONS] },
      { key: "heading-style", label: "Heading style", type: "select", options: [...HEADING_STYLE_OPTIONS] },
      { key: "heading-case", label: "Heading casing", type: "select", options: [...HEADING_CASE_OPTIONS] },
      { key: "heading-tracking", label: "Heading letter spacing", type: "text" },
      { key: "font-body", label: "Body font", type: "font" },
      { key: "typography-scale", label: "Scale (minimum)", type: "scale" },
      { key: "typography-scale-max", label: "Scale (maximum)", type: "scale" },
    ],
  },
  {
    id: "spacing",
    label: "Spacing",
    description: "The spacing scale sections and components are laid out with.",
    fields: [
      { key: "space-1", label: "Space 1", type: "text" },
      { key: "space-2", label: "Space 2", type: "text" },
      { key: "space-3", label: "Space 3", type: "text" },
      { key: "space-4", label: "Space 4", type: "text" },
      { key: "space-5", label: "Space 5", type: "text" },
      { key: "space-6", label: "Space 6", type: "text" },
      { key: "space-7", label: "Space 7", type: "text" },
      { key: "space-8", label: "Space 8", type: "text" },
      { key: "space-10", label: "Space 10", type: "text" },
      { key: "space-12", label: "Space 12", type: "text" },
      { key: "space-16", label: "Space 16", type: "text" },
    ],
  },
  {
    id: "effects",
    label: "Effects",
    description: "Shadows and motion timing.",
    fields: [
      { key: "shadow-sm", label: "Shadow (small)", type: "text" },
      { key: "shadow-md", label: "Shadow (medium)", type: "text" },
      { key: "transition-fast", label: "Transition (fast)", type: "text" },
      { key: "transition-base", label: "Transition (base)", type: "text" },
    ],
  },
  {
    id: "layout",
    label: "Layout",
    description: "Page width and horizontal padding.",
    fields: [
      { key: "container-max", label: "Container max width", type: "text" },
      { key: "container-pad", label: "Container padding", type: "text" },
    ],
  },
];

export interface ContrastPair {
  id: string;
  label: string;
  /** Foreground token key, or a literal colour via fgLiteral. */
  fg?: string;
  fgLiteral?: string;
  /** Background token key. */
  bg: string;
}

// The foreground/background combinations the published site actually renders,
// checked against the tenant's accessibility target in the design editor.
// "accent on bg" is body link text; button labels are fixed white over the
// accent. Text colours here are the calculated (derived) ones.
export const CONTRAST_PAIRS: ContrastPair[] = [
  { id: "body", label: "Body text", fg: "text", bg: "bg" },
  { id: "muted", label: "Muted text", fg: "text-muted", bg: "bg" },
  { id: "links", label: "Link text", fg: "accent", bg: "bg" },
  { id: "header", label: "Header text", fg: "header-text", bg: "header-bg" },
  { id: "header-links", label: "Header links", fg: "accent", bg: "header-bg" },
  { id: "footer", label: "Footer text", fg: "footer-text", bg: "footer-bg" },
  { id: "button", label: "Button label", fgLiteral: "#ffffff", bg: "accent" },
];
